import {useState, useEffect, Dispatch, SetStateAction, useMemo} from 'react'
import usePostApi from '../../custom_hooks/usePostApi'
import {useThemeMode} from '../../../../_metronic/partials'
import {Formik, Form, Field, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import ThemeColors from '../ThemePref'
import Select, {components} from 'react-select'
import {KTIcon} from '../../../../_metronic/helpers'
import Swal from 'sweetalert2'

type Data = {
  pod_business_name: string
  pod_brand_name: string
  pod_industry_id: number | string
  pod_team_size: number
  pod_description: string
  pod_country_code: string
  pod_website_url: string
  dateFormat: string
  timeZone: string
  pod_pref_color: string
  pod_currency_code: string
  data_center_id: string
}

interface IDataCenters {
  data: Data
  setData: Dispatch<SetStateAction<Data>>
  isPodAdded: boolean
  podData: {pod_code: string}
  fetchPODsList: () => void
}

const DataCenters = ({data, setData, isPodAdded, podData, fetchPODsList}: IDataCenters) => {
  const {execute, loading} = usePostApi()

  const [dataCenters, setDataCenters] = useState([])

  const getDataCenters = async () => {
    try {
      const url = '/master/datacenter/get'
      const formData = {}

      const response = await execute(url, 'POST', formData)
      setDataCenters(response.data)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  useEffect(() => {
    getDataCenters()
  }, [])

  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const perPage = 4

  const filteredData = useMemo(() => {
    return dataCenters?.filter(
      (dc: any) =>
        dc.dc_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dc.nicename.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [searchTerm, dataCenters])

  const paginatedData = useMemo(() => {
    const start = (page - 1) * perPage
    return filteredData.slice(start, start + perPage)
  }, [filteredData, page])

  const totalPages = Math.ceil(filteredData.length / perPage)

  const getCurrentView = () => {
    if (isPodAdded) {
      Swal.fire({
        icon: 'success',
        title: 'POD Created Successfully!',
        html: `
      <p class="mb-0">
        Congratulations. Your POD has been created successfully. 
        <br />
        <strong>Your POD ID: ${podData?.pod_code}</strong>
      </p>
    `,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          popup: 'swal2-custom-popup',
        },
      }).then(() => {
        // Call refreshPods when user closes the modal
        fetchPODsList()
      })

      return null // Prevent rendering anything else while Swal is active
    } else if (loading) {
      return (
        <div className='h-100px m-auto d-flex flex-column justify-content-center align-items-center'>
          <div
            className='spinner-border spinner-primary mr-15'
            style={{color: 'var(--bs-primary)'}}
          ></div>
        </div>
      )
    } else if (dataCenters.length === 0) {
      return (
        <div className='h-100px m-auto d-flex flex-column justify-content-center align-items-center'>
          <p className='mb-1 text-muted fw-semibold'> No data found.</p>
          <button
            onClick={() => getDataCenters()}
            className='btn btn-sm btn-light-primary d-flex align-items-center'
          >
            <i className='ki-solid ki-arrows-circle fs-4'></i>
            Reload
          </button>
        </div>
      )
    } else {
      return (
        <>
          <label className='d-flex align-items-center fs-6 fw-semibold form-label mb-2'>
            <span className='required'>Select Your Preferred Data Center</span>
            <i
              className='fas fa-info-circle ms-2 fs-7 text-muted'
              data-bs-toggle='tooltip'
              title='This data center will be used to host your resources. Choose the location that’s closest to your users for better performance.'
            ></i>
          </label>

          <div className='d-flex align-items-center position-relative mb-4'>
            <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-3' />
            <input
              type='search'
              id='kt_filter_search'
              className='form-control form-control-white ps-9 fw-normal'
              placeholder='Search by name or location...'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setPage(1)
              }}
              autoComplete='off'
            />
          </div>

          <div className='d-flex flex-wrap'>
            {paginatedData.map((dc: any, index) => (
              <div key={dc.dc_id} className={`w-50 mb-4 ${index % 2 === 0 ? 'pe-2' : 'ps-2'}`}>
                <div
                  className={`d-flex border border-dashed rounded p-3 cursor-pointer ${
                    data.data_center_id === dc.dc_id
                      ? 'border-primary bg-light'
                      : 'border-secondary'
                  }`}
                  onClick={() => {
                    setData((prev: Data) => ({...prev, data_center_id: dc.dc_id}))
                  }}
                  role='button'
                >
                  <div className='d-flex align-items-center'>
                    <input
                      type='radio'
                      className='form-check-input me-2 custom-radio'
                      name='datacenter'
                      checked={data.data_center_id === dc.dc_id}
                      readOnly
                    />
                  </div>
                  <div className='ms-1'>
                    <h6 className='mb-0 fw-semibold'>{dc.dc_name}</h6>
                    <div className='text-muted small'>
                      <div>
                        <strong className='fw-semibold'>Country:</strong> {dc.nicename || '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {paginatedData.length === 0 && (
              <div className='text-muted text-center'>No data centers found.</div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className='d-flex justify-content-center mt-4'>
              <nav>
                <ul className='pagination'>
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button
                      className='page-link'
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    >
                      Previous
                    </button>
                  </li>
                  {Array.from({length: totalPages}, (_, i) => (
                    <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                      <button className='page-link' onClick={() => setPage(i + 1)}>
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                    <button
                      className='page-link'
                      onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )
    }
  }

  return <div className='container'>{getCurrentView()}</div>
}

export {DataCenters}
